import * as React from 'react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Command } from 'lucide-react';

import styled from 'styled-components';

import { MenuItems } from 'types';
import { ExitButton, UserInfo } from 'atoms';
import { MenuItemList, LanguageSwitcher } from 'molecules';

import { Colors } from 'services/ColorService';

export interface IMenuComponent {
  activeMenu?: MenuItems;
  menuItems: ReactNode[];
  onExit: () => void;
  /** Opens the command palette — a pointer entry point for touch / no-keyboard. */
  onOpenPalette: () => void;
  /**
   * Колокольчик уведомлений (ТЗ 0068) — СЛОТОМ, а не встроенным контейнером.
   *
   * Этот компонент презентационный и имеет сториз; контейнер, тянущий данные
   * через Apollo, сломал бы их. Тот же приём уже применён к `menuItems`.
   */
  notifications?: ReactNode;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  /** ТЗ 0083: клик по имени — «Мой профиль» (рабочая почта, правила). */
  onOpenProfile?: () => void;
}

export const MenuComponent = ({
  menuItems,
  onExit,
  onOpenPalette,
  notifications,
  userFirstName,
  userLastName,
  userEmail,
  onOpenProfile,
}: IMenuComponent) => {
  const { t } = useTranslation();
  const fullName = [userFirstName, userLastName].filter(Boolean).join(' ');
  // Пока пользователь не загрузился, кнопка была бы пустой рамкой.
  const who = fullName || userEmail || '';
  const profileLabel = t('profile.openFor', { name: who });

  return (
    <Wrapper>
      <Container>
        <MenuItemList>{menuItems}</MenuItemList>
        <RightGroup>
          {notifications}
          <PaletteTrigger
            type="button"
            onClick={onOpenPalette}
            title={t('commandPalette.help.openTrigger')}
            aria-label={t('commandPalette.help.openTrigger')}
          >
            <Command size={20} strokeWidth={2} />
          </PaletteTrigger>
          <LanguageSwitcher />
          {who && (
            <ProfileTrigger
              type="button"
              onClick={onOpenProfile}
              disabled={!onOpenProfile}
              title={
                fullName && userEmail
                  ? `${profileLabel}\n${userEmail}`
                  : profileLabel
              }
              aria-label={profileLabel}
            >
              <UserInfo
                firstName={userFirstName}
                lastName={userLastName}
                email={userEmail}
              />
            </ProfileTrigger>
          )}
          <ExitButton onClick={onExit} />
        </RightGroup>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: ${Colors.White};
  padding: 21px 2px;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: inherit;
`;

/** Зазор 12, а не 16: шапке нужно место под шесть групп меню на 1280 px. */
const RightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaletteTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 8px;
  cursor: pointer;
  color: ${Colors.Gray};
  background: ${Colors.White};
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: ${Colors.Blue};
    border-color: ${Colors.Blue};
  }
`;

const ProfileTrigger = styled.button`
  padding: 4px 6px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  text-align: right;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${Colors.SecondLightGray};
  }

  &:focus-visible {
    outline: none;
    border-color: ${Colors.Blue};
  }

  &:disabled {
    cursor: default;
  }
`;
